import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import {
    type MarkerColorsConstantType,
    type MarkerDynamicStyleType,
    type MarkerSizeConstantType,
    type MarkerMeasures,
    type MarkerColors,
    type MarkerType,
    type MarkerSize
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    getFocusRingStyle,
    STATE_COLOR_KEYS
} from "../../helpers/theme/state";
import {
    getElevationStyle
} from "../../helpers/theme/elevation";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    getHitArea
} from "../../helpers/theme/control";
import {
    webStyle
} from "../../utils";

export const MARKER_TYPOGRAPHIES: Record<MarkerSize, keyof NCoreUIKit.Typography> = {
    medium: "bodyMediumSize",
    small: "bodySmallSize"
};

export const MARKER_SEPARATOR_TYPOGRAPHY: keyof NCoreUIKit.Typography = "labelSmallSize";

export const MARKER_TEXT_COLOR_KEYS: Record<MarkerType, keyof NCoreUIKit.TextContentColors> = {
    warning: "warning",
    danger: "danger",
    neutral: "mid",
    success: "mid",
    info: "mid"
};

export const getMarkerSize = ({
    typography,
    variant,
    size
}: MarkerSizeConstantType): MarkerMeasures => {
    const currentTypography = variant === "separator" ? MARKER_SEPARATOR_TYPOGRAPHY : MARKER_TYPOGRAPHIES[size];

    return {
        lineHeight: typography[currentTypography].lineHeight ?? 0,
        typography: currentTypography
    };
};

export const getMarkerIconColor = ({
    colors,
    type
}: Pick<MarkerColorsConstantType, "colors" | "type">): string => {
    if(type !== "warning" && type !== "danger") {
        return colors.content.icon.mid;
    }

    return colors.system.default[STATE_COLOR_KEYS[type] as "error" | "warning"];
};

export const getMarkerColors = ({
    isUnread,
    variant,
    colors,
    type
}: MarkerColorsConstantType): MarkerColors => {
    const isSeparator = variant === "separator";

    const separatorTextColor = isUnread ? colors.content.text.high : colors.content.text.low;

    return {
        textColor: isSeparator ? separatorTextColor : colors.content.text[MARKER_TEXT_COLOR_KEYS[type]],
        separatorColor: isUnread ? colors.content.border.default : colors.content.border.subtle,
        emphasizedColor: colors.content.text.high,
        lineColor: colors.content.border.subtle,
        iconColor: getMarkerIconColor({
            colors,
            type
        })
    };
};

const stylesheet = StyleSheet.create({
    pressable: {
        justifyContent: "center",
        alignSelf: "flex-start",
        maxWidth: "100%",
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    pillSurface: {
        pointerEvents: "none",
        borderStyle: "solid",
        ...StyleSheet.absoluteFill
    },
    container: {
        alignItems: "flex-start",
        flexDirection: "row",
        width: "100%",
        minWidth: 0
    },
    separator: {
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "row",
        minWidth: 0
    },
    surface: {
        alignItems: "flex-start",
        borderStyle: "solid",
        flexDirection: "row",
        maxWidth: "100%"
    },
    pill: {
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "row",
        maxWidth: "100%",
        flexShrink: 1,
        minWidth: 0
    },
    iconSlot: {
        justifyContent: "center",
        alignItems: "center",
        flexShrink: 0
    },
    line: {
        flexShrink: 1,
        flexGrow: 1
    },
    content: {
        flexShrink: 1,
        minWidth: 0
    },
    text: {
        flexShrink: 1
    },
    lineStuck: {
        minWidth: 0
    }
});

export const useStyles = ({
    isFocusVisible,
    currentColors,
    shapeConfigs,
    touchTargets,
    currentSize,
    pointerType,
    shadowAlpha,
    elevations,
    isCentered,
    isSticky,
    radiuses,
    borders,
    variant,
    colors,
    spaces
}: MarkerDynamicStyleType) => {
    const hitArea = getHitArea({
        visualSize: currentSize.lineHeight + spaces.spacingXs * 2,
        touchTargets,
        pointerType
    });

    const styles = {
        pill: {
            minHeight: currentSize.lineHeight + spaces.spacingXxs * 2,
            paddingHorizontal: spaces.spacingSm
        } as Mutable<ViewStyle>,
        pillSurface: {
            backgroundColor: colors.content.container.raised,
            borderColor: colors.content.border.subtle,
            ...getCornerStyle({
                radius: radiuses.chip,
                shapeConfigs
            }),
            borderWidth: borders.line
        } as Mutable<ViewStyle>,
        line: {
            backgroundColor: currentColors.separatorColor,
            minWidth: spaces.spacingLg,
            height: borders.separator
        } as Mutable<ViewStyle>,
        iconSlot: {
            height: currentSize.lineHeight
        } as Mutable<ViewStyle>,
        surface: {
            ...getCornerStyle({
                radius: radiuses.actions,
                shapeConfigs
            }),
            padding: spaces.spacingXs,
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        pressable: {
            minHeight: hitArea.minHeight,
            margin: -spaces.spacingXs
        } as Mutable<ViewStyle>,
        container: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>
    };

    if(variant === "border") {
        Object.assign(styles.container, {
            borderBottomColor: colors.content.border.subtle,
            borderBottomWidth: borders.separator,
            paddingBottom: spaces.spacingSm
        });
    }

    if(isCentered) {
        styles.container.justifyContent = "center";
        styles.pressable.alignSelf = "center";
    }

    if(isSticky) {
        Object.assign(styles.container, webStyle({
            top: spaces.spacingSm,
            position: "sticky"
        }), {
            zIndex: shapeConfigs.layers.raised
        });
    }

    if(variant === "separator") {
        styles.container.gap = 0;
    }

    Object.assign(styles.pillSurface, getElevationStyle({
        shadowColor: colors.system.shadow,
        level: "level2",
        shadowAlpha,
        elevations
    }));

    Object.assign(styles.surface, getFocusRingStyle({
        isFocusVisible,
        borders,
        colors
    }));

    return styles;
};
export default stylesheet;
